import { CompozzError, isCompozzError } from "@dto/CompozzError";
import type User from "@dto/Users";
import { BASE_URL } from "@utils/constants";
import { del } from "@utils/utils";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { translate } from "src/i18n/i18n";

export interface DeleteUserParams {
  tenantKey: string;
  user: User;
  users: Map<string, User>;
  updateUsers: (users: Map<string, User>) => void;
  t: TFunction<"translation", undefined>;
}

/**
 * deleteUserRequest deletes a user through the admin API.
 * On success it removes the user from the local user map and raises a success
 * toast; on failure it raises an error toast (translating the backend error
 * when an i18n code is provided). Returns true when the deletion succeeded.
 */
export async function deleteUserRequest({ tenantKey, user, users, updateUsers, t }: DeleteUserParams): Promise<boolean> {
  const response = await del<void>(`${BASE_URL}/admin/users`, {
    tenant: tenantKey,
    username: user.username,
  });

  if (response && isCompozzError(response)) {
    const cError = response as CompozzError;
    toast.error(cError.i18n ? translate(t, cError.i18n, cError.params) : cError.message);
    return false;
  }

  const newUsers = new Map(users);
  newUsers.delete(user._key);
  updateUsers(newUsers);
  toast.success(t("users.delete.success"));
  return true;
}
